/** The products module's part of the vendor dashboard (`dashboard.tiles` / `dashboard.tasks` slots of the vendor panel). */

import { useQuery } from "@tanstack/react-query";

import { vendorProductsCounts, vendorStockSummary } from "@motolynk/api-client";
import { DashboardTask, usePermissions } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { KpiTile } from "@motolynk/ui";

import { vendorProductKeys } from "./source";

const summaryKey = ["vendor", "stock", "summary"] as const;

function useCounts(enabled = true) {
  return useQuery({
    queryKey: vendorProductKeys.counts(),
    queryFn: async () => (await vendorProductsCounts()).data,
    enabled,
  });
}

function useStockSummary(enabled = true) {
  return useQuery({
    queryKey: summaryKey,
    queryFn: async () => (await vendorStockSummary()).data,
    enabled,
  });
}

export function VendorProductsTile() {
  const { t } = useTranslation("products");
  const counts = useCounts().data;
  return (
    <KpiTile
      icon="package"
      label={t("vendor.dashboard.products")}
      value={counts ? counts.approved : "—"}
      hint={
        counts
          ? t("vendor.dashboard.productsHint", {
              pending: counts.pending_review,
              drafts: counts.draft,
            })
          : undefined
      }
    />
  );
}

export function VendorStockTile() {
  const { t } = useTranslation("products");
  const summary = useStockSummary().data;
  return (
    <KpiTile
      icon="box"
      label={t("vendor.dashboard.stock")}
      value={summary ? summary.units : "—"}
      hint={
        summary
          ? t("vendor.dashboard.stockHint", { low: summary.low, out: summary.out })
          : undefined
      }
    />
  );
}

/** Rows only show when something waits (DashboardTask hides a zero count). */
export function VendorProductTasks() {
  const { t } = useTranslation("products");
  const { can } = usePermissions();
  const counts = useCounts(can("products.manage")).data;
  const summary = useStockSummary(can("inventory.manage")).data;
  return (
    <>
      <DashboardTask
        to="/products?status=changes_requested"
        icon="package"
        label={t("vendor.dashboard.changesRequested")}
        count={counts?.changes_requested}
        tone="warning"
      />
      <DashboardTask
        to="/products?status=rejected"
        icon="alert"
        label={t("vendor.dashboard.rejected")}
        count={counts?.rejected}
      />
      <DashboardTask
        to="/products?status=draft"
        icon="file"
        label={t("vendor.dashboard.drafts")}
        count={counts?.draft}
      />
      <DashboardTask
        to="/stock?stock=out"
        icon="box"
        label={t("vendor.dashboard.out")}
        count={summary?.out}
        tone="warning"
      />
      <DashboardTask
        to="/stock?stock=low"
        icon="box"
        label={t("vendor.dashboard.low")}
        count={summary?.low}
      />
    </>
  );
}
